import React, { createContext, ReactNode, useCallback, useContext, useState } from 'react';

/**
 * Cross-mount state store for coach mark tours.
 *
 * Why this exists: tour components gate their render on external predicates
 * — feature flags, async home-details loading state, target DOM presence.
 * When any of those flips false mid-flow (e.g. a refetch of home-details
 * briefly swaps the header nav to a Skeleton, unmounting the coach mark
 * target elements), the tour component returns null and its local useState
 * is destroyed. When the predicate flips back true, the component remounts
 * with fresh state, losing track of whether the user had already clicked
 * past the welcome modal. The symptom: modal appears → user clicks Next →
 * no coach mark ever shows (2 of 5 times, per QA).
 *
 * Holding the tour's `phase` in a context mounted at app root decouples it
 * from the tour component's lifecycle. Remounts resume exactly where they
 * left off.
 *
 * Keyed by tour id so multiple tours can coexist — three tours share this
 * provider today (FlipGive Participant plus two upcoming).
 */

export type TourPhase = 'welcome' | 'tour' | 'restarting' | 'done';

type ContextValue = {
    phases: Record<string, TourPhase>;
    setPhase: (tourId: string, phase: TourPhase) => void;
};

const CoachMarkTourContext = createContext<ContextValue | null>(null);

export const CoachMarkTourProvider = ({ children }: { children: ReactNode }) => {
    const [phases, setPhases] = useState<Record<string, TourPhase>>({});

    const setPhase = useCallback((tourId: string, phase: TourPhase) => {
        setPhases((prev) => {
            if (prev[tourId] === phase) {
                return prev;
            }
            return { ...prev, [tourId]: phase };
        });
    }, []);

    return (
        <CoachMarkTourContext.Provider value={{ phases, setPhase }}>
            {children}
        </CoachMarkTourContext.Provider>
    );
};

/**
 * Read and write a single tour's phase. Defaults to 'welcome' when the tour
 * hasn't been touched yet.
 */
export const useCoachMarkTourPhase = (tourId: string) => {
    const ctx = useContext(CoachMarkTourContext);
    if (!ctx) {
        throw new Error('useCoachMarkTourPhase must be used within a <CoachMarkTourProvider>.');
    }
    return {
        phase: ctx.phases[tourId] ?? ('welcome' as TourPhase),
        setPhase: (phase: TourPhase) => ctx.setPhase(tourId, phase),
    };
};
